import { useState, useEffect } from 'react';
import styled from 'styled-components';

import {
  Flex,
  Text,
  ModalV2,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalAction,
  RadioGroup,
  ColorsV2,
  getToken,
  spaces,
  type Address,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import AddressOptionCard from './AddressOptionCard';
import { formatAddressForDisplay } from './helpers';
import type { AddressValidationResult } from './useAddressValidation';

const StyledRadioGroup = styled(RadioGroup)`
  flex-direction: column;

  & span {
    margin-right: 0;
    border-color: ${getToken(ColorsV2.GRAY_DARK)};
  }

  & :checked + span {
    border-color: ${getToken(ColorsV2.GRAY_DARK)} !important;
    background: radial-gradient(
      circle,
      ${getToken(ColorsV2.GRAY_DARK)} 0%,
      ${getToken(ColorsV2.GRAY_DARK)} 50%,
      transparent 50%,
      transparent
    ) !important;
  }
`;

const AddressOptions = {
  VALIDATED: 'validated',
  ORIGINAL: 'original',
};

type AddressOption = (typeof AddressOptions)[keyof typeof AddressOptions];

type AddressValidationModalProps = {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: (selectedAddress: Address) => void;
  originalAddress: Address;
  validationResult: AddressValidationResult | null;
};

const AddressValidationModal = ({
  isVisible,
  onClose,
  onConfirm,
  originalAddress,
  validationResult,
}: AddressValidationModalProps) => {
  const { t } = useTranslation('common');
  const [selectedOption, setSelectedOption] = useState<AddressOption>(
    AddressOptions.ORIGINAL
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasValidatedAddress, setHasValidatedAddress] = useState(false);

  useEffect(() => {
    if (isVisible && originalAddress) {
      if (validationResult) {
        setHasValidatedAddress(true);
        setSelectedOption(AddressOptions.VALIDATED);
      } else {
        setHasValidatedAddress(false);
        setSelectedOption(AddressOptions.ORIGINAL);
      }
    }
  }, [isVisible, originalAddress, validationResult]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const selectedAddress =
        selectedOption === AddressOptions.VALIDATED && validationResult
          ? validationResult.addressComponents
          : originalAddress;

      await onConfirm(selectedAddress);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setHasValidatedAddress(false);
    setSelectedOption(AddressOptions.ORIGINAL);
    setIsLoading(false);
    onClose();
  };

  return (
    <ModalV2
      visible={isVisible}
      hide={handleClose}
      ariaLabelledby="address-validation-modal-header"
      minWidth={spaces.space_5xl_rem}>
      <ModalHeader p={spaces.space_xs}>
        <Text variant="heading-2">{t('addressValidationModal.title')}</Text>
      </ModalHeader>
      <ModalBody p={spaces.space_l}>
        <Flex vertical>
          {hasValidatedAddress ? (
            <StyledRadioGroup
              value={selectedOption}
              onChange={(value: AddressOption) => setSelectedOption(value)}
              gap={spaces.space_m}
              vertical>
              <AddressOptionCard
                id="validated-address-option"
                isSelected={selectedOption === AddressOptions.VALIDATED}
                onClick={() => setSelectedOption(AddressOptions.VALIDATED)}
                isRecommended
                label={t('addressValidationModal.validatedAddressLabel')}
                address={validationResult?.addressComponents || ({} as Address)}
              />
              <AddressOptionCard
                id="original-address-option"
                isSelected={selectedOption === AddressOptions.ORIGINAL}
                onClick={() => setSelectedOption(AddressOptions.ORIGINAL)}
                label={t('addressValidationModal.originalAddressLabel')}
                address={originalAddress}
              />
            </StyledRadioGroup>
          ) : (
            <Flex vertical gap={spaces.space_l}>
              <Text variant="body">
                {t('addressValidationModal.confirmPrompt')}
              </Text>
              <Flex vertical>
                <Text variant="label-large">
                  {formatAddressForDisplay(originalAddress).street}
                </Text>
                <Text variant="label-large">
                  {formatAddressForDisplay(originalAddress).location}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter p={spaces.space_m}>
        <ModalFooterActions>
          <ModalAction
            id="address-validation-modal-cancel-button"
            variant="text"
            disabled={isLoading}
            onClick={handleClose}>
            {t('addressValidationModal.cancel')}
          </ModalAction>
          <ModalAction
            id="address-validation-modal-confirm-button"
            loading={isLoading}
            disabled={isLoading}
            onClick={handleConfirm}>
            {t('addressValidationModal.confirm')}
          </ModalAction>
        </ModalFooterActions>
      </ModalFooter>
    </ModalV2>
  );
};

export default AddressValidationModal;
